/**
 * Pure date maths for the stay range picker (check-in and check-out).
 *
 * Every date is a `YYYY-MM-DD` string and every sum is done in UTC, so no
 * visitor time zone or daylight-saving jump can move a day. "Today" is never
 * read here: callers pass the Morocco day in, which keeps these functions
 * deterministic and keeps the clock out of server rendering.
 */

export interface StayRange {
  checkIn: string | null;
  checkOut: string | null;
}

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDate(value: string | null | undefined): value is string {
  return !!value && ISO_RE.test(value);
}

function toUtc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function fromUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDaysIso(iso: string, days: number): string {
  const d = toUtc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

/** Whole nights from check-in to check-out; 0 when either is missing. */
export function nightsBetween(checkIn: string | null, checkOut: string | null): number {
  if (!isIsoDate(checkIn) || !isIsoDate(checkOut)) return 0;
  return Math.max(0, Math.round((toUtc(checkOut).getTime() - toUtc(checkIn).getTime()) / 86_400_000));
}

/** 0 for Monday through 6 for Sunday: the week starts on Monday. */
export function mondayIndex(iso: string): number {
  return (toUtc(iso).getUTCDay() + 6) % 7;
}

export interface MonthRef {
  year: number;
  /** 0-based, like Date. */
  month: number;
}

export function monthOf(iso: string): MonthRef {
  const m = ISO_RE.exec(iso)!;
  return { year: Number(m[1]), month: Number(m[2]) - 1 };
}

export function shiftMonth(ref: MonthRef, delta: number): MonthRef {
  const total = ref.year * 12 + ref.month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

/** Negative when `a` is before `b`, 0 for the same month. */
export function compareMonths(a: MonthRef, b: MonthRef): number {
  return a.year * 12 + a.month - (b.year * 12 + b.month);
}

/**
 * The cells of one month, Monday first: `null` pads the days before the 1st
 * so each date lands under its weekday.
 */
export function monthCells(ref: MonthRef): (string | null)[] {
  const first = `${ref.year}-${String(ref.month + 1).padStart(2, "0")}-01`;
  const days = new Date(Date.UTC(ref.year, ref.month + 1, 0)).getUTCDate();
  const cells: (string | null)[] = Array.from({ length: mondayIndex(first) }, () => null);
  for (let d = 1; d <= days; d++) cells.push(addDaysIso(first, d - 1));
  return cells;
}

/**
 * What a tap on `iso` does to the range.
 *
 * The first tap sets the check-in. The next tap on a later day sets the
 * check-out. A tap on or before the check-in, or any tap once a range is
 * complete, starts again from that day. A check-out closer than `minNights`
 * is ignored (the picker already greys those days out).
 */
export function nextRange(current: StayRange, iso: string, minNights = 1): StayRange {
  const { checkIn, checkOut } = current;
  if (!checkIn || checkOut || iso <= checkIn) return { checkIn: iso, checkOut: null };
  if (nightsBetween(checkIn, iso) < Math.max(1, minNights)) return current;
  return { checkIn, checkOut: iso };
}

export type StayPresetId = "this_weekend" | "next_weekend" | "one_week";

/**
 * The quick ranges, from the Morocco day `today`.
 *
 * A weekend is Friday night to Sunday morning. On a Saturday "this weekend"
 * is the one night left; on a Sunday it is already over, so it is the coming
 * Friday. "Next weekend" is always the one after that. "1 week" is seven
 * nights from the chosen check-in when there is one, otherwise from today.
 */
export function stayPresets(today: string, checkIn?: string | null): Record<StayPresetId, { checkIn: string; checkOut: string }> {
  const dow = mondayIndex(today); // Fri = 4, Sat = 5, Sun = 6
  let thisStart: string;
  let thisEnd: string;
  if (dow === 6) {
    thisStart = addDaysIso(today, 5);
    thisEnd = addDaysIso(today, 7);
  } else if (dow === 5) {
    thisStart = today;
    thisEnd = addDaysIso(today, 1);
  } else {
    thisStart = addDaysIso(today, 4 - dow);
    thisEnd = addDaysIso(thisStart, 2);
  }
  const thisFriday = dow === 5 ? addDaysIso(today, -1) : thisStart;
  const nextStart = addDaysIso(thisFriday, 7);
  const weekStart = isIsoDate(checkIn) && checkIn >= today ? checkIn : today;
  return {
    this_weekend: { checkIn: thisStart, checkOut: thisEnd },
    next_weekend: { checkIn: nextStart, checkOut: addDaysIso(nextStart, 2) },
    one_week: { checkIn: weekStart, checkOut: addDaysIso(weekStart, 7) },
  };
}
